import { Component, OnDestroy, ViewEncapsulation, HostBinding } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription, Observable, merge, switchMap, tap, map, catchError } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { toString as luceneToString, term, NodeExpression, OperatorExpression, FieldExpression } from 'lucene';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';
import { MediaMatcher } from '@angular/cdk/layout';

import { BatchesService } from '../../../order/services/batches.service';
import { QualityIssueService, ISSUE_FIELDS } from '../../../claim/services/quality-issue.service';
import { OrderService } from '../../../order/services/order.service';
import { ShipmentsService } from '../../../order/services/shipments.service';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import { Document, FieldOfDocument, OrderNumbers } from '../../../commercial-documents/models/document';
import { PaginatedResult } from '../../models/paginated-result';
import { TranslationService } from '../../services/translation.service';
import { pageTransitions } from '../../animations/page-transitions';
import { FieldOfOrder, Order } from '../../../order/models/order';
import {
	QualityIssue,
	ClaimStatus,
	PENDING_STATUS_LIST,
	CLOSED_STATUS_LIST
} from '../../../claim/models/quality-issue';
import { UserService } from '../../services/user.service';
import { Batch, FieldOfBatch } from '../../../order/models/batch';
import { Shipment, FieldOfShipment } from '../../../order/models/shipment';
import { User } from '../../models/user';
import { PRODUCT_OPTIONS, ORDER_NUMBER_OPTIONS, WEIGHT_OPTIONS } from '../../models/list';
import { GROSS, ARTICLE, CUSTOMER } from '../../models/settings';
import { OrderByDirection, OrderBy } from '../../utils/constant';
import { OfferLine } from '../../../offer/models/offer';
import { OfferService } from '../../../offer/services/offer.api.service';
import { QualityIssueFilterService } from '../../../claim/services/quality-issue-filter.service';
import { FieldOfScheduleBusinessOrder, ScheduleBusinessOrder } from '../../../order/models/schedule-business-order';
import { ScheduleBusinessOrderService } from '../../../order/services/schedule-business-order.service';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { ScrollerDirective } from '../../directives/scroller.directive';
import { NumberPipe } from '../../pipes/number.pipe';
import { FastLaneService } from '../../../fastlane/services/fastlane.service';
import { FastLaneItems } from '../../../fastlane/models/fastlane-datacenter-config';
import { FieldOfFastLaneItems } from '../../../fastlane/models/fastlane.model';
import { DatacenterType, UserDatacenterView } from '../../models/datacenter-aggrid-view';
import { DatacenterFilterType } from '../../models/datacenter-filters';
import { DatacenterDefaultConfiguration } from '../../datacenter/datacenter-default-configuration';
import { GuaranteeRequestService, REQUEST_FIELDS } from '../../../guarantee/services/guarantee-request.service';
import { Guarantee } from '../../../guarantee/models/guarantee';

import { ResultCardGuaranteeComponent } from './result-card/result-card-guarantee.component';
import { ResultCardFastLaneComponent } from './result-card/result-card-fastlane.component';
import { ResultCardOrderAcknowledgementComponent } from './result-card/result-card-order-acknowledgment.component';
import { ResultCardCertificateComponent } from './result-card/result-card-certificate.component';
import { ResultCardInvoiceComponent } from './result-card/result-card-invoice.component';
import { ResultCardDispatchAdviceComponent } from './result-card/result-card-dispatch-advice.component';
import { ResultCardShipmentComponent } from './result-card/result-card-shipment.component';
import { ResultCardBatchComponent } from './result-card/result-card-batch.component';
import { ResultCardClaimComponent } from './result-card/result-card-claim.component';
import { ResultCardSBOrderComponent } from './result-card/result-card-sb-business-order.component';
import { ResultCardOrderComponent } from './result-card/result-card-order.component';
import { ResultCardOfferComponent } from './result-card/result-card-offer.component';

class SearchResult<SearchResultName, SearchResultEntry> {
	constructor(
		readonly name: SearchResultName,
		readonly result: PaginatedResult<SearchResultEntry>
	) {}
}

type SearchResultType = SearchResult<'order', Order>
	| SearchResult<'sbOrder', ScheduleBusinessOrder>
	| SearchResult<'claim', QualityIssue>
	| SearchResult<'guarantee', Guarantee>
	| SearchResult<'batch', Batch>
	| SearchResult<'shipment', Shipment>
	| SearchResult<'offerDraft', OfferLine>
	| SearchResult<'offer', OfferLine>
	| SearchResult<'dispatch-advice', Document>
	| SearchResult<'invoice', Document>
	| SearchResult<'certificate', Document>
	| SearchResult<'order-acknowledgment', Document>
	| SearchResult<'fastlane', FastLaneItems>
	;

interface SectionState {
	loading: boolean;
	result?: SearchResultType;
}

interface ServiceSearchOperator<SearchResultName> {
	name: SearchResultName;
	serviceCode: string;
	resultGroupTitle: string;
	resultPageName: string;
	retrieveTopEntriesAndCount(searchText: string): Observable<SearchResultType & { name: SearchResultName }>;
}

type ServiceSearchOperatorType = ServiceSearchOperator<'order'>
	| ServiceSearchOperator<'sbOrder'>
	| ServiceSearchOperator<'claim'>
	| ServiceSearchOperator<'guarantee'>
	| ServiceSearchOperator<'batch'>
	| ServiceSearchOperator<'shipment'>
	| ServiceSearchOperator<'offerDraft'>
	| ServiceSearchOperator<'offer'>
	| ServiceSearchOperator<'dispatch-advice'>
	| ServiceSearchOperator<'invoice'>
	| ServiceSearchOperator<'certificate'>
	| ServiceSearchOperator<'order-acknowledgment'>
	| ServiceSearchOperator<'fastlane'>;

@Component({
	selector: 'copo-search-results',
	templateUrl: './search-results.component.html',
	animations: pageTransitions,
	styleUrl: './search-results.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		LoadingSpinnerComponent,
		NgIf,
		TranslateModule,

		MatButtonModule,
		NgFor,
		MatRippleModule,
		NumberPipe,
		ResultCardOfferComponent,
		ResultCardOrderComponent,
		ResultCardSBOrderComponent,
		ResultCardClaimComponent,
		ResultCardGuaranteeComponent,
		ResultCardBatchComponent,
		ResultCardShipmentComponent,
		ResultCardDispatchAdviceComponent,
		ResultCardInvoiceComponent,
		ResultCardCertificateComponent,
		ResultCardFastLaneComponent,
		ResultCardOrderAcknowledgementComponent,
	],
})
export class SearchResultsComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	mediaQuery: MediaQueryList;
	selectedResultName: string | undefined;
	searchStarted = false;
	sectionStates = new Map<string, SectionState>();
	accessToOnlyOneSearchResult = false;
	readonly searchOperators: ServiceSearchOperatorType[] = [];
	orderRequiredFields: FieldOfOrder[] = [
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'thickness',
		'width',
		'length',
		'cddWeek',
		'productDescription'
	];

	fastlaneItemFields: FieldOfFastLaneItems[] = [
		'customerRequestName',
		'supplierRfqNumber',
		'lastModifiedDate',
		'prototypeName',
		'prototypeStatus',
		'requestedSteelGrade',
		'form',
		'thickness',
		'width',
		'length',
		'requestedQuantity',
		'requestedQuantityUnitDescription',
		'requestedDeliveryDate',
		'id'
	];

	sbOrderRequiredFields: FieldOfScheduleBusinessOrder[] = [
		'supplierOrderNumber',
		'customerOrderNumber',
		'customerArticleReference',
		'businessStatus',
		'steelgradeDescription'
	];

	batchRequiredFields: FieldOfBatch[] = [
		'id',
		'supplierOrderNumber',
		'supplierOrderItemNumber',
		'thickness',
		'width',
		'length',
		'cddWeek',
		'statusGroup',
		'statusGroupDescription',
		'status',
		'statusDescription',
		'currentLocation',
		'currentLocationDescription',
		'batchReference'
	];

	shipmentRequiredFields: FieldOfShipment[] = [
		'dispatchAdviceNumber',
		'soldToDescription',
		'qtyBatchBrutConcat',
		'transportReferenceConcat',
		'transportModeDescription',
		'transportMode',
		'qtyBatchNetConcat',
		'qtyShipmentBrut',
		'qtyShipmentNet',
		'transportMode',
		'shipTo',
		'shipToDescription',
		'shipmentReference',
		'supplierShipmentNumber'
	];

	documentRequiredFields: readonly FieldOfDocument[] = [
		'customerOrderReference',
		'supplierOrderReference',
		'soldtoName',
		'shiptoName',
		'id',
		'type',
		'subtype',
		'date',
		'name',
		'paymentDueDate',
		'supplierVatNumber',
		'payerName',
		'payerVatNumber',
		'invoiceAmount',
		'invoiceAmountWithoutVat',
		'invoiceCurrency',
		'invoiceTaxAmount',
		'invoicedQuantity',
		'documentLines',
		'transportMode',
		'millCode'
	];

	user: User;
	paramName: string | undefined;
	productDisplay: keyof typeof PRODUCT_OPTIONS = ARTICLE;
	orderDisplay: keyof typeof ORDER_NUMBER_OPTIONS = CUSTOMER;
	weightDisplay: keyof typeof WEIGHT_OPTIONS = GROSS;
	orderNumbers: OrderNumbers[] = [];

	private readonly cachedServiceResultsBySearchText = new Map<string, Map<string, SearchResultType>>();

	private readonly searchTextSubscription: Subscription;
	private searchText: string | undefined;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly activatedRoute: ActivatedRoute,

		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,
		private readonly orderService: OrderService,
		private readonly qualityIssueService: QualityIssueService,
		private readonly guaranteeRequestService: GuaranteeRequestService,
		private readonly batchService: BatchesService,
		private readonly offerService: OfferService,
		private readonly shipmentService: ShipmentsService,
		private readonly fastLaneService: FastLaneService,
		private readonly qualityIssueFilterService: QualityIssueFilterService,
		private readonly documentService: DocumentService,
		private readonly scheduleBusinessOrderService: ScheduleBusinessOrderService,
		private readonly media: MediaMatcher,
		private readonly router: Router,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly route: ActivatedRoute
	) {
		this.mediaQuery = this.media.matchMedia('screen and (width <= 768px)');
		this.user = this.userService.user;

		this.orderRequiredFields.push(
			'grossQtyOrdered',
			'grossQtyInProduction',
			'grossQtyAvailableReadyToShip',
			'grossQtyAvailableInTransit',
			'grossQtyDelivered'
		);
		this.sbOrderRequiredFields.push(
			'qtyInProduction',
			'qtyAvailableReadyToShip',
			'qtyAvailableInTransit',
		);
		this.batchRequiredFields.push('qtyBatchGross');
		this.orderRequiredFields.push('customerOrderNumber', 'customerOrderItemNumber');
		this.batchRequiredFields.push('customerOrderNumber', 'customerOrderItemNumber');
		this.orderRequiredFields.push('steelgradeDescription');
		this.batchRequiredFields.push('steelgradeDescription');

		// OFFER - Draft
		this.searchOperators.push({
			name: 'offerDraft',
			serviceCode: 'EOFFR',
			resultGroupTitle: 'eoffer.offers.draft',
			resultPageName: 'offers/draft',
			retrieveTopEntriesAndCount: searchText => this.offerService.getOfferLinePage(
				searchText,
				['DRAFT'],
				false,
				4,
				'INLINE'
			).pipe(
				catchError((err: unknown) => {
					console.error('error retrieving offers', err);
					return of({ results: [], count: 0, page: 1, limit: 1 });
				}),
				map(paginatedResult => new SearchResult('offerDraft', paginatedResult))
			)
		}, { // OFFER
			name: 'offer',
			serviceCode: 'EOFFR',
			resultGroupTitle: 'eoffer.offer.plural',
			resultPageName: 'offers/all',
			retrieveTopEntriesAndCount: searchText => this.offerService.getOfferLinePage(
				searchText,
				['REQUESTED', 'OFFERED', 'ACCEPTED'],
				false,
				4,
				'INLINE'
			).pipe(
				catchError((err: unknown) => {
					console.error('error retrieving offers', err);
					return of({ results: [], count: 0, page: 1, limit: 1 });
				}),
				map(paginatedResult => new SearchResult('offer', paginatedResult))
			)
		}, { // FASTLANE
			name: 'fastlane',
			serviceCode: 'PFAST',
			resultGroupTitle: 'global.search.fastlane-entry',
			resultPageName: 'fastlane',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.fastLaneService.createQuickSerachFilter(searchText);
				return this.fastLaneService.getFastLaneItems(
					this.fastlaneItemFields,
					luceneFilter,
					1,
					4,
					new OrderBy('customerRequestName', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving fastlane', err);
						const emptyOrderArray: FastLaneItems[] = [];
						return of({ results: emptyOrderArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('fastlane', paginatedResult))
				);
			}
		}, { // ORDER
			name: 'order',
			serviceCode: 'PORDT',
			resultGroupTitle: 'order.orders',
			resultPageName: 'orders',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.orderService.createLuceneFilter(searchText);
				return this.orderService.getOrders(
					this.orderRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('cdd', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving orders', err);
						const emptyOrderArray: Order[] = [];
						return of({ results: emptyOrderArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('order', paginatedResult))
				);
			}
		}, { // SB-ORDER
			name: 'sbOrder',
			serviceCode: 'PORDT',
			resultGroupTitle: 'datacenter.schedule-business-order',
			resultPageName: 'schedule-business-orders',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.scheduleBusinessOrderService.createQuickSerachFilter(searchText);
				return this.scheduleBusinessOrderService.getScheduleBusinessOrders(
					this.sbOrderRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('customerOrderNumber', OrderByDirection.ASC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving orders', err);
						const emptyOrderArray: ScheduleBusinessOrder[] = [];
						return of({ results: emptyOrderArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('sbOrder', paginatedResult))
				);
			}
		},	{ // CLAIM
			name: 'claim',
			serviceCode: 'QUAPP',
			resultGroupTitle: 'issue.qualityissues',
			resultPageName: 'claims',
			retrieveTopEntriesAndCount: searchText => {
				const { user } = this.userService;
				const isReader = user.isClaimReader();
				const isDetector = user.isClaimDetector();
				const isValidator = user.isClaimValidator();
				const viewDraft = isDetector || isValidator || isReader;
				const viewPendingAndClosed = isValidator || isReader;

				const visibleStatusList: ClaimStatus[] = [];
				const closedStatuses: ClaimStatus[] = [];
				if (viewDraft) {
					visibleStatusList.push(ClaimStatus.DRAFT);
				}
				if (viewPendingAndClosed) {
					visibleStatusList.push(...PENDING_STATUS_LIST);
					closedStatuses.push(...CLOSED_STATUS_LIST);
				}

				if (visibleStatusList.length === 0) {
					return of(new SearchResult<'claim', QualityIssue>('claim', {
						results: [],
						count: 0,
						page: 1,
						limit: 1
					}));
				}
				return this.qualityIssueService.getIssues(
					ISSUE_FIELDS,
					luceneToString(this.createQuery(searchText, visibleStatusList, closedStatuses)),
					1,
					4,
					new OrderBy('modificationDate', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving issues', err);
						const emptyQualityIssueArray: QualityIssue[] = [];
						return of({ results: emptyQualityIssueArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('claim', paginatedResult))
				);
			}
		}, { // GUARANTEE
			name: 'guarantee',
			serviceCode: 'PGNTE',
			resultGroupTitle: 'search.guarantees',
			resultPageName: 'guarantee-requests',
			retrieveTopEntriesAndCount: searchText => this.guaranteeRequestService.getRequests(
				REQUEST_FIELDS,
				this.createGuaranteeLuceneFilter(searchText),
				0,
				4,
				[new OrderBy('requestDate', OrderByDirection.DESC)],
				false,
				false
			).pipe(
				catchError((err: unknown) => {
					console.error('error retrieving guarantee requests', err);
					const emptyGuaranteeArray: Guarantee[] = [];
					return of({ results: emptyGuaranteeArray, count: 0, page: 1, limit: 1 });
				}),
				map(paginatedResult => new SearchResult('guarantee', paginatedResult))
			)
		}, { // BATCH
			name: 'batch',
			serviceCode: 'PORDT',
			resultGroupTitle: 'search.batches',
			resultPageName: 'batches',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.batchService.createLuceneFilter(searchText);

				return this.batchService.getBatches(
					this.batchRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('batchReference', OrderByDirection.ASC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving orders', err);
						const emptyBatchArray: Batch[] = [];
						return of({ results: emptyBatchArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('batch', paginatedResult))
				);
			}
		}, { // SHIPMENT
			name: 'shipment',
			serviceCode: 'PORDT',
			resultGroupTitle: 'search.shipments',
			resultPageName: 'shipments',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.shipmentService.createLuceneFilter(searchText);

				return this.shipmentService.getDatacenterShipments(
					this.shipmentRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('dispatchAdviceNumber', OrderByDirection.DESC),
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving orders', err);
						const emptyShipmentArray: Shipment[] = [];
						return of({ results: emptyShipmentArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('shipment', paginatedResult))
				);
			}
		}, { // Dispatch-Advice
			name: 'dispatch-advice',
			serviceCode: 'PDOCS',
			resultGroupTitle: 'search.dispatch.advices',
			resultPageName: 'dispatch-advices',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.documentService.createLuceneFilter(searchText, 'DA');

				return this.documentService.getDocuments(
					this.documentRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('date', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving dispatch advices', err);
						const emptyDispatchAdviceArray: Document[] = [];
						return of({ results: emptyDispatchAdviceArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('dispatch-advice', paginatedResult))
				);
			}
		}, { // Invoice
			name: 'invoice',
			serviceCode: 'PDOCS',
			resultGroupTitle: 'search.invoices',
			resultPageName: 'invoice',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.documentService.createLuceneFilter(searchText, 'IN');

				return this.documentService.getDocuments(
					this.documentRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('date', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving invoices', err);
						const emptyInvoiceArray: Document[] = [];
						return of({ results: emptyInvoiceArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('invoice', paginatedResult))
				);
			}
		}, { // Certificate
			name: 'certificate',
			serviceCode: 'PDOCS',
			resultGroupTitle: 'search.certificates',
			resultPageName: 'certificate',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.documentService.createLuceneFilter(searchText, 'CE');

				return this.documentService.getDocuments(
					this.documentRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('date', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving certificates', err);
						const emptyCertificateArray: Document[] = [];
						return of({ results: emptyCertificateArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('certificate', paginatedResult))
				);
			}
		}, { // Order-Acknowledgment
			name: 'order-acknowledgment',
			serviceCode: 'PDOCS',
			resultGroupTitle: 'search.order.acknowledgments',
			resultPageName: 'order-acknowledgments',
			retrieveTopEntriesAndCount: searchText => {
				const luceneFilter = this.documentService.createLuceneFilter(searchText, 'OA');

				return this.documentService.getDocuments(
					this.documentRequiredFields,
					luceneFilter,
					1,
					4,
					new OrderBy('date', OrderByDirection.DESC),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error('error retrieving order acknowledgements', err);
						const emptyOrderAcknowledgArray: Document[] = [];
						return of({ results: emptyOrderAcknowledgArray, count: 0, page: 1, limit: 1 });
					}),
					map(paginatedResult => new SearchResult('order-acknowledgment', paginatedResult))
				);
			}
		});
		if (this.searchOperators.filter(op => this.hasAccess(op)).length === 1) {
			this.accessToOnlyOneSearchResult = true;
		}

		// searchText observer
		this.searchTextSubscription = this.activatedRoute.params
			.pipe(
				map(params => <string | undefined>params['query']),
				tap(searchText => {
					this.searchText = searchText;
					this.searchStarted = Boolean(searchText);
					if (searchText) {
						this.initSectionStates(searchText);
					}
				}),
				switchMap(searchText => searchText ? this.search(searchText) : of(null))
			)
			.subscribe({
				next: result => {
					if (result) {
						this.setCachedResult(this.searchText || '', result);
						this.sectionStates.set(result.name, { loading: false, result });
					}
				},
				error: () => {
					this.snackBar.open(
						this.translationService.getTR('quapp.error'),
						undefined,
						{
							duration: 3000
						}
					);
				}
			});
	}

	getUniqueOrderName(document: Document): OrderNumbers[] {
		const orderReference = new Set<string>();
		for (const docLine of document.documentLines) {
			if (docLine.customerOrderItemReference) {
				orderReference.add(`${ docLine.customerOrderReference }/${ docLine.customerOrderItemReference }`);
			} else {
				orderReference.add(docLine.customerOrderReference);
			}
		}
		for (const orderRef of orderReference) {
			const orderNumber = {
				key: orderRef,
				isSelected: false
			};
			this.orderNumbers.push(orderNumber);
		}
		return this.orderNumbers;
	}

	ngOnDestroy() {
		this.searchTextSubscription.unsubscribe();
	}

	getServiceTitle(name: SearchResultType['name']) {
		return this.getServiceSearchOperator(name).resultGroupTitle;
	}

	filterResults(name?: SearchResultType['name']) {
		this.selectedResultName = name;
	}

	hasAccess(op: ServiceSearchOperatorType) {
		if (op.serviceCode === 'PDOCS') {
			this.paramName = op.name;
			switch (this.paramName) {
			case 'certificate':
				return this.user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
			case 'dispatch-advice':
				return this.user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True');
			case 'invoice':
				return this.user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
			case 'order-acknowledgment':
				return this.user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
			default:
				break;
			}
		}
		if (op.serviceCode === 'PORDT' && op.name === 'sbOrder') {
			return this.user.hasServiceParameterEqualsTo('PORDT', 'SBOrder', 'True');
		}
		return this.userService.user.hasService(op.serviceCode);
	}

	async navigateToServiceResults(name: SearchResultType['name']) {
		const route = await this.goToServiceResults(name);
		if (route.length > 0) {
			await this.router.navigate(route, { queryParams: { search: this.searchText }, replaceUrl: true });
		}
	}

	async goToServiceResults(name: SearchResultType['name']): Promise<string[]> {
		if (this.mediaQuery.matches || name === 'offerDraft' || name === 'offer' || name === 'guarantee') {
			const section = this.sectionStates.get(name);
			if (!section?.result || section.result.result.count === 0) {
				return [];
			}
			if (name === 'guarantee') {
				return ['/guarantee-requests/list'];
			}
			const servicePageName = this.getServiceSearchOperator(name).resultPageName;
			return [
				'/search',
				this.searchText || '',
				...servicePageName.split('/')
			];
		}
		await this.redirectToDataCenter(name);
		return [];
	}

	getSectionState(name: string): SectionState | undefined {
		return this.sectionStates.get(name);
	}

	private createGuaranteeLuceneFilter(searchText: string): string {
		const wildcard = `*${ term.escape(searchText) }*`;
		const nameField: FieldExpression = { field: 'name', term: wildcard, quoted: false };
		const guaranteeIdField: FieldExpression = { field: 'guaranteeId', term: wildcard, quoted: false };
		const nameOrGuaranteeId: NodeExpression = {
			left: nameField,
			operator: 'OR',
			right: guaranteeIdField,
			parenthesized: true
		};
		const notDraft: OperatorExpression = {
			start: 'NOT',
			left: { field: 'status', term: 'Draft', quoted: true }
		};
		const globalNode: NodeExpression = {
			left: nameOrGuaranteeId,
			operator: 'AND',
			right: notDraft,
			parenthesized: false
		};
		return luceneToString(globalNode);
	}

	private getDataCenterInfo(name: SearchResultType['name']) {
		let url: string;
		let datacenterType: DatacenterType;
		let documentType = '';

		switch (name) {
		case 'order':
			url = '/order-tracking/order/datacenter';
			datacenterType = 'order';
			break;
		case 'sbOrder':
			url = '/order-tracking/schedule-business-order/datacenter';
			datacenterType = 'schedule-business-order';
			break;
		case 'claim':
			url = '/quality-issues/datacenter';
			datacenterType = 'quality';
			break;
		case 'batch':
			url = '/order-tracking/batch/datacenter';
			datacenterType = 'batch';
			break;
		case 'shipment':
			url = '/order-tracking/shipment/datacenter';
			datacenterType = 'shipment';
			break;
		case 'offer':
			url = '';
			datacenterType = 'offer';
			break;
		case 'dispatch-advice':
			url = '/commercial-documents/datacenter';
			documentType = 'DA';
			datacenterType = 'document';
			break;
		case 'invoice':
			url = '/commercial-documents/datacenter';
			documentType = 'IN';
			datacenterType = 'document';
			break;
		case 'certificate':
			url = '/commercial-documents/datacenter';
			documentType = 'CE';
			datacenterType = 'document';
			break;
		case 'order-acknowledgment':
			url = '/commercial-documents/datacenter';
			documentType = 'OA';
			datacenterType = 'document';
			break;
		case 'fastlane':
			url = '/fastlane/prototype/datacenter';
			datacenterType = 'prototypes';
			break;
		default:
			throw new Error(`${ name } datacenter not found`);
		}

		return { url, datacenterType, documentType };
	}

	private async redirectToDataCenter(name: SearchResultType['name']) {
		if (!this.searchText) {
			return;
		}

		const { url, datacenterType, documentType } = this.getDataCenterInfo(name);

		const defaultView = this.defaultConfiguration.getDefaultView(datacenterType);
		const filtersList: DatacenterFilterType[] = [];
		const globalSearch: DatacenterFilterType = {
			id: 'globalSearch',
			values: [this.searchText]
		};

		if (datacenterType === 'document') {
			const documentTypeFilter = this.getDocumentTypeFilter(documentType);
			filtersList.push(documentTypeFilter);
		}
		if (datacenterType === 'quality') {
			const { user } = this.userService;
			const isReader = user.isClaimReader();
			const isDetector = user.isClaimDetector();
			const isValidator = user.isClaimValidator();
			const viewDraft = isDetector || isValidator || isReader;
			const viewPendingAndClosed = isValidator || isReader;

			const statusFilter = this.buildStatusFilter(viewDraft, viewPendingAndClosed);
			if (statusFilter) {
				filtersList.push(statusFilter);
			}
		}
		filtersList.push(globalSearch);
		defaultView.filterState = filtersList;
		await this.navigation(defaultView, url);
	}

	private buildStatusFilter(viewDraft: boolean, viewPendingAndClosed: boolean): DatacenterFilterType | null {
		const visibleStatusList: ClaimStatus[] = [];
		const closedStatuses: ClaimStatus[] = [];

		if (viewDraft) {
			visibleStatusList.push(ClaimStatus.DRAFT);
		}
		if (viewPendingAndClosed) {
			visibleStatusList.push(...PENDING_STATUS_LIST);
			closedStatuses.push(...CLOSED_STATUS_LIST);
		}

		if (visibleStatusList.length > 0 || closedStatuses.length > 0) {
			const statusFilterValues = [
				...visibleStatusList.map(status => ({
					apiName: 'status',
					selected: true,
					value: status.toLowerCase()
				})),
				...closedStatuses.map(status => ({
					apiName: 'status',
					selected: true,
					value: status.toLowerCase()
				}))
			];

			return {
				id: 'status',
				values: [statusFilterValues]
			};
		}

		return null;
	}

	private getDocumentTypeFilter(documentType: string): DatacenterFilterType {
		const documentTypeMap: Record<string, { code: string; translation: string }> = {
			DA: { code: 'DA', translation: this.translationService.getTR('datacenter-filter.dispatch-advice') },
			IN: { code: 'IN', translation: this.translationService.getTR('commercial-documents.invoice') },
			CE: { code: 'CE', translation: this.translationService.getTR('order.card.document.certificates') },
			OA: { code: 'OA', translation: this.translationService.getTR('order.card.document.order.acknowledgments') }
		};

		return {
			id: 'type',
			values: [documentTypeMap[documentType]]
		};
	}

	private async navigation(defaultView: UserDatacenterView, routePath: string) {
		await this.router.navigate([routePath], {
			relativeTo: this.route,
			state: { ...this.previousState, view: defaultView }
		});
	}

	private getServiceSearchOperator(name: SearchResultType['name']) {
		const opFound = this.searchOperators.find(op => op.name === name);
		if (!opFound) {
			throw new Error(`service search opeartor not found for ${ name }`);
		}
		return opFound;
	}

	private search(searchText: string): Observable<SearchResultType> {
		return merge(
			...this.searchOperators
				.filter(op => this.hasAccess(op))
				.map(op => {
					const cached = this.getCachedResult(searchText, op.name);
					return cached
						? of(cached)
						: op.retrieveTopEntriesAndCount(searchText);
				})
		);
	}

	private getCachedResult(searchText: string, name: SearchResultType['name']): SearchResultType | undefined {
		const cachedServiceResults = this.cachedServiceResultsBySearchText.get(searchText);
		return cachedServiceResults ? cachedServiceResults.get(name) : undefined;
	}

	private initSectionStates(searchText: string) {
		const nextStates = new Map<string, SectionState>();
		for (const op of this.searchOperators.filter(opFilter => this.hasAccess(opFilter))) {
			const cached = this.getCachedResult(searchText, op.name);
			nextStates.set(op.name, cached
				? { loading: false, result: cached }
				: { loading: true });
		}
		this.sectionStates = nextStates;
	}

	private setCachedResult(searchText: string, result: SearchResultType) {
		let cachedServiceResults = this.cachedServiceResultsBySearchText.get(searchText);
		if (!cachedServiceResults) {
			cachedServiceResults = new Map<SearchResultType['name'], SearchResultType>();
			this.cachedServiceResultsBySearchText.set(searchText, cachedServiceResults);
		}
		cachedServiceResults.set(result.name, result);
	}

	private createQuery(search: string, statuses: ClaimStatus[], closedStatus: ClaimStatus[]) {
		if (closedStatus.length > 0) {
			return this.qualityIssueFilterService.concatFilters(
				this.qualityIssueFilterService.generateExpresionForSearch(search),
				this.qualityIssueFilterService.concatFilters(
					this.qualityIssueFilterService.createFiltersFromClaimStatusList(statuses),
					this.qualityIssueFilterService.createFiltersFromClaimStatusList(closedStatus),
					'OR'
				),
				'AND'
			);
		}
		return this.qualityIssueFilterService.concatFilters(
			this.qualityIssueFilterService.generateExpresionForSearch(search),
			this.qualityIssueFilterService.createFiltersFromClaimStatusList(statuses),
			'AND'
		);
	}
}
